/**
 * Public pricing — the only place in the app where a price or an allowance is a
 * constant rather than a server response.
 *
 * CONTRACT GAP: the contract exposes plan terms, pack prices and credit prices
 * through `GET /v1/billing` and `GET /v1/credits`, both of which require a
 * session. The marketing pages (landing, funnel) are shown to signed-out
 * visitors, so they have no authenticated source for the same figures. The
 * smallest API change that closes this is an unauthenticated
 * `GET /v1/pricing` returning the `plans`, `packs` and `prices` blocks that
 * `GET /v1/billing` and `GET /v1/credits` already assemble — no new data, just
 * the public subset behind no cookie.
 *
 * Until that exists, every signed-out price lives here, mirroring
 * API_SPEC.md §8 and PRODUCT_BACKEND_MAP.md §7. Every *signed-in* screen reads
 * the server instead — none of these values are used behind the app shell.
 */
import { money } from './viewModel';

export const PUBLIC_PRICING = {
  /** Plans on sale, in cents and credits a month, per `GET /v1/billing` → `plans.pro.terms` (owner, 2026-10-15). Starter has no quarterly plan. */
  tiers: {
    starter: { monthly: 900, credits: 30, autoApplyDaily: 0 },
    pro: { monthly: 2900, quarterly: 6900, credits: 150, autoApplyDaily: 10 },
    power: { monthly: 4900, quarterly: 13500, credits: 400, autoApplyDaily: 20 },
  },
  /** Pro's monthly allowance, for the screens that say "Pro includes N credits a month". */
  proMonthlyCredits: 150,
  freeGrantCredits: 5,
  /** Operation prices, per `GET /v1/credits` → `prices`. */
  prices: { analysis: 0, tailor: 1, autoApply: 2 },
  /** Daily Auto-Apply ceilings, per `GET /v1/auto-apply` → `limits.dailyCapMax`. Free has none. */
  dailyCapMax: { free: 0, pro: 10 },
} as const;

const p = PUBLIC_PRICING;
const T = p.tiers;

/** Labels the signed-out pages render, derived once so they cannot drift apart. */
export const publicPricingLabels = () => {
  const autoApply = (n: number) => (n > 0 ? 'Auto-Apply, ' + n + ' a day (coming soon)' : 'No Auto-Apply');
  return {
    freePrice: money(0),
    freeGrantLabel: p.freeGrantCredits + ' credits',
    freeGrantNote: 'to start, they never expire',
    starterPrice: money(T.starter.monthly),
    starterCreditsLabel: T.starter.credits + ' credits',
    starterCreditsFootnote: 'That is ' + Math.floor(T.starter.credits / p.prices.tailor) + ' tailored resumes.',
    starterAutoApplyLabel: autoApply(T.starter.autoApplyDaily),
    proPrice: money(T.pro.monthly),
    proTermsNote: 'or ' + money(T.pro.quarterly) + ' every 3 months',
    proSaveNote: 'save ' + money(T.pro.monthly * 3 - T.pro.quarterly),
    proCreditsLabel: T.pro.credits + ' credits',
    proCreditsFootnote: 'That is ' + Math.floor(T.pro.credits / p.prices.tailor) + ' tailored resumes.',
    proAutoApplyLabel: autoApply(T.pro.autoApplyDaily),
    costAnalysis: String(p.prices.analysis),
    costTailor: String(p.prices.tailor),
    costAutoApply: String(p.prices.autoApply),
    costTailorNote: 'Tailor a resume',
    costTailorValue: p.prices.tailor + (p.prices.tailor === 1 ? ' credit' : ' credits'),
    // Auto-Apply is not sold until it works (legal-pages ticket 08, owner decision D8).
    costAutoApplyValue: 'coming soon',
    refundNote: 'If a tailor run fails, its credit comes straight back.',
  };
};
